export type GenericRecord = Record<string, unknown>;

/**
 * Best-effort string conversion for values with no statically-known shape
 * (e.g. JSON-typed API fields declared as `unknown`). Falls back to
 * JSON.stringify for objects/arrays so they don't render as '[object Object]'.
 */
export function safeStringify(value: unknown): string {
  if (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean' ||
    typeof value === 'bigint'
  ) {
    return String(value);
  }
  try {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- lib.d.ts types JSON.stringify as always returning string, but it really returns undefined for undefined, functions, and symbols
    return JSON.stringify(value) ?? String(value);
  } catch {
    return '[Unserializable value]';
  }
}

export function toRecord(value: unknown): GenericRecord | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as GenericRecord;
}

/**
 * Structural equality for JSON-shaped values (what a form or a JSON API field
 * holds): objects compare by key set and values, arrays by position, the rest
 * by `===`. Key order is ignored — a document that went through the backend's
 * JSONB comes back with its keys re-sorted.
 */
export function isJsonEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
    return a.every((item, index) => isJsonEqual(item, b[index]));
  }
  const recordA = toRecord(a);
  const recordB = toRecord(b);
  if (!recordA || !recordB) return false;
  const keysA = Object.keys(recordA);
  if (keysA.length !== Object.keys(recordB).length) return false;
  return keysA.every(key => Object.hasOwn(recordB, key) && isJsonEqual(recordA[key], recordB[key]));
}

export function parseJsonIfString(value: unknown): unknown {
  if (typeof value !== 'string') return value;
  const trimmed = value.trim();
  if (!trimmed) return value;
  try {
    return JSON.parse(trimmed);
  } catch {
    return value;
  }
}

export function getString(record: GenericRecord, key: string): string | null;
export function getString(value: unknown, keys: string[]): string | null;
export function getString(valueOrRecord: unknown, keyOrKeys: string | string[]): string | null {
  if (typeof keyOrKeys === 'string') {
    const record = toRecord(valueOrRecord);
    if (!record) return null;
    const value = record[keyOrKeys];
    if (typeof value !== 'string') return null;
    return value.trim().length > 0 ? value : null;
  }

  const record = toRecord(valueOrRecord);
  if (!record) return null;
  for (const key of keyOrKeys) {
    const candidate = record[key];
    if (typeof candidate === 'string' && candidate.length > 0) return candidate;
  }
  return null;
}

export function getNumber(value: unknown, keys: string[]): number | null {
  const record = toRecord(value);
  if (!record) return null;

  for (const key of keys) {
    const candidate = record[key];
    if (typeof candidate === 'number' && Number.isFinite(candidate)) return candidate;
    if (typeof candidate === 'string') {
      const parsed = Number(candidate);
      if (Number.isFinite(parsed)) return parsed;
    }
  }

  return null;
}
